// CLAPPY — the canonical geometry. This file is the ONLY place his shape is
// described; everything that draws him reads it from here.
//
// Why this exists (2026-08-25): he was born on 2026-08-24 with his geometry
// written out longhand in three places — the CtwBot component, five inline
// marks on calltowrap.com, and whatever rendered the four PWA icons. The third
// was the worst of them: only the PNG outputs were committed, so his icon form
// could not be reproduced at all, let alone kept in step. Three hand-copies of
// the same path data, identical only by luck, one of them write-only.
//
// A mascot that drifts between the advertising and the app breaks the same
// continuity contract the accent does: what you saw is what you signed into.
//
// The form, as Huan settled it:
//   - a clapperboard at a glance, a robot on inspection;
//   - stripes in the BOARD's green over the stick's deeper green (paper-white
//     read "too noticeable and dramatic");
//   - browed eyes, variant B of four, at the original eye line;
//   - the stick IN FRONT of the board ("I prefer the website version with it
//     in front") — his correction after the first app render stacked it behind.

/** The side of the square everything is authored in. */
export const CLAPPY_SQUARE = 18;

/** Everything is authored in this square. Never rescale the numbers — set width/height. */
export const CLAPPY_VIEWBOX = `0 0 ${CLAPPY_SQUARE} ${CLAPPY_SQUARE}`;

/** The board: the body he is mostly made of. */
export const CLAPPY_BOARD = { x: 1, y: 5, width: 16, height: 12, rx: 2.5 } as const;

/** The stick's face — the angled bar that hinges at its left end and claps. */
export const CLAPPY_STICK_FACE = 'M1.5 5.5 L16.5 2.2 L17 4.4 L2 7.7 Z';

/** The four stripes riding on the stick, in the board's lighter green. */
export const CLAPPY_STRIPES =
  'M3.00 5.17 L5.40 4.64 L6.72 6.66 L4.33 7.19 Z ' +
  'M6.60 4.38 L9.00 3.85 L10.33 5.87 L7.93 6.40 Z ' +
  'M10.20 3.59 L12.60 3.06 L13.93 5.08 L11.53 5.60 Z ' +
  'M13.80 2.79 L16.20 2.27 L17.52 4.28 L15.12 4.81 Z';

/**
 * The eyes — browed, variant B. Cut as true holes (an SVG mask) wherever he
 * sits on a coloured surface, so the surface shows through rather than a
 * painted-on white that only matches one background.
 */
export const CLAPPY_EYE_LEFT = 'M4.38 11.60 A1.8 1.8 0 1 1 7.65 11.22 Z';
export const CLAPPY_EYE_RIGHT = 'M13.62 11.60 A1.8 1.8 0 1 0 10.35 11.22 Z';

/** The capture ring CtwBot pulses while listening. Not part of the static mark. */
export const CLAPPY_RING = { cx: 9, cy: 10, r: 8.1, strokeWidth: 1.1 } as const;

export interface ClappyMarkOptions {
  /** Board green. Defaults to the CallToWrap accent. */
  board?: string;
  /** Stick green — deeper than the board, so the stripes read against it. */
  stick?: string;
  /** Painted eye colour. Ignored when `cutEyes` is true. */
  eye?: string;
  /**
   * Cut the eyes as holes instead of painting them. Right for a glyph on an
   * unknown surface; wrong for a PWA icon, which needs an opaque face.
   */
  cutEyes?: boolean;
  /** Square canvas size in px. Omit for a viewBox-only, scalable mark. */
  size?: number;
  /** Fills the whole square behind him — PWA icons must not be transparent. */
  background?: string;
  /**
   * Inset him within the square, 0..0.5 of the side. Maskable icons need their
   * subject inside the safe zone or a launcher crops his stick off.
   */
  padding?: number;
}

export const CLAPPY_BOARD_GREEN = '#2e6b4e';
export const CLAPPY_STICK_GREEN = '#224f3a';
export const CLAPPY_EYE_PAPER = '#f8f4ec';

/**
 * The static mark as a standalone SVG document — the one renderer for the
 * favicon, the site's marks and the PWA icons. CtwBot draws the same constants
 * itself because it animates the parts independently.
 */
export function clappyMarkSvg(options: ClappyMarkOptions = {}): string {
  const {
    board = CLAPPY_BOARD_GREEN,
    stick = CLAPPY_STICK_GREEN,
    eye = CLAPPY_EYE_PAPER,
    cutEyes = false,
    size,
    background,
    padding = 0,
  } = options;

  const b = CLAPPY_BOARD;
  const dim = size === undefined ? '' : ` width="${size}" height="${size}"`;

  // Padding is applied by widening the viewBox around the same 18-unit square,
  // so he shrinks within the canvas and every path below stays untouched.
  const pad = Math.max(0, Math.min(0.5, padding)) * 18 * 2;
  const viewBox = pad === 0
    ? CLAPPY_VIEWBOX
    : `${-pad / 2} ${-pad / 2} ${18 + pad} ${18 + pad}`;

  const eyes =
    `<path d="${CLAPPY_EYE_LEFT}" fill="${cutEyes ? '#000' : eye}"/>` +
    `<path d="${CLAPPY_EYE_RIGHT}" fill="${cutEyes ? '#000' : eye}"/>`;

  const bg = background === undefined
    ? ''
    : `<rect x="${-pad / 2}" y="${-pad / 2}" width="${18 + pad}" height="${18 + pad}" fill="${background}"/>`;

  const boardRect = `<rect x="${b.x}" y="${b.y}" width="${b.width}" height="${b.height}" rx="${b.rx}" fill="${board}"`;

  // Paint order is the mark's order: board, then the stick IN FRONT of it
  // (Huan's correction), then the stripes riding the stick, then the eyes.
  // Cut eyes have to be a mask on the board instead, so they resolve first.
  const boardPainted = cutEyes
    ? `<mask id="clappy-eyes"><rect x="${-pad / 2}" y="${-pad / 2}" width="${18 + pad}" height="${18 + pad}" fill="#fff"/>${eyes}</mask>` +
      `${boardRect} mask="url(#clappy-eyes)"/>`
    : `${boardRect}/>`;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}"${dim}>` +
    bg +
    boardPainted +
    `<path d="${CLAPPY_STICK_FACE}" fill="${stick}"/>` +
    `<path d="${CLAPPY_STRIPES}" fill="${board}"/>` +
    (cutEyes ? '' : eyes) +
    `</svg>`
  );
}
